import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IconSparkles,
  IconSparklesFilled,
  IconTemplate,
  IconTemplateFilled,
} from "@tabler/icons-react";
import NavRoomsIcon from "../../../assets/icons/nav/nav-rooms.svg?react";
import NavRoomsFilledIcon from "../../../assets/icons/nav/nav-rooms-filled.svg?react";
import { AnalyticsNavElementName } from "../../../tools/analytics/analytics.types";
import { navClickBottomBar } from "../../../tools/analytics/domains/nav.analytics";
import { scrollDiscoveryToTop } from "../../../domains/app/stores/discoveryScrollStore";
import { useAudioContext } from "../../../domains/app/contexts/audio.context";
import { useIsDesktopView } from "../../../common/hooks/useIsDesktopView";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { Badge } from "../../ui/Badge";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useApiQuery } from "../../../services/api";
import { roomHasNewContent } from "../../../domains/room/utils/roomVisits";
import { useHasUnseenVisualAbstracts } from "../../../features/visual-abstract/unseenVisualAbstracts";
import Skeleton from "../../skeleton/Skeleton";
import SkeletonTextLine from "../../skeleton/SkeletonTextLine";

const GPT_NAV_ITEM = {
  to: "/gpt/chat/new",
  activePrefix: "/gpt",
  label: "JuisciGPT",
  ActiveIcon: IconSparklesFilled,
  InactiveIcon: IconSparkles,
  analyticsName: AnalyticsNavElementName.GPT,
} as const;

const ROOMS_NAV_ITEM = {
  to: "/rooms",
  activePrefix: "/rooms",
  label: "Rooms",
  ActiveIcon: NavRoomsFilledIcon,
  InactiveIcon: NavRoomsIcon,
  analyticsName: AnalyticsNavElementName.ROOMS,
} as const;

const EXPLORE_NAV_ITEM = {
  to: "/discovery",
  activePrefix: "/discovery",
  label: "Explore",
  ActiveIcon: IconTemplateFilled,
  InactiveIcon: IconTemplate,
  analyticsName: AnalyticsNavElementName.DISCOVERY,
} as const;

/** JuisciGPT leads the bar, and `/discovery` is billed as "Explore". */
const NAV_ITEMS = [GPT_NAV_ITEM, EXPLORE_NAV_ITEM, ROOMS_NAV_ITEM] as const;

type NavItem = (typeof NAV_ITEMS)[number];

type NavItemLabel = NavItem["label"];

/**
 * The red dot on a tab, per tab.
 *
 * Rooms answers "a room you follow has content you have not seen", JuisciGPT
 * answers "a poster you asked for has finished" — two different questions with
 * two different sources, computed together because one record of what the bar
 * is showing is easier to reason about than two hooks racing to decorate it.
 */
export function useNavBadges(): Record<NavItemLabel, boolean> {
  const { currentUser } = useCurrentUser();
  const query = useApiQuery(
    "/webapp/private/rooms/new-content",
    currentUser === null ? false : {}
  );
  const hasUnseenVisualAbstracts = useHasUnseenVisualAbstracts();

  const anyNewRoomContent =
    query.state === "success" &&
    Object.entries(query.body).some(([id, date]) =>
      roomHasNewContent(date, id)
    );

  return {
    JuisciGPT: hasUnseenVisualAbstracts,
    Explore: false,
    Rooms: anyNewRoomContent,
  };
}

export default function Navbar({
  hiddenOnDesktop = true,
  badges: badgesProp,
}: {
  hiddenOnDesktop?: boolean;
  /** Storybook only: pins every dot so the states are inspectable. */
  badges?: Record<NavItemLabel, boolean>;
}): React.JSX.Element | null {
  const autoBadges = useNavBadges();
  const badges = badgesProp === undefined ? autoBadges : badgesProp;
  const { updateMiniPlayerStyles } = useAudioContext();
  const { isDesktop } = useIsDesktopView();
  const { t } = useTranslation();
  const navLabels: Record<NavItemLabel, string> = {
    JuisciGPT: "JuisciGPT",
    Explore: t("Explore"),
    Rooms: t("Rooms"),
  };
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      containerRef.current &&
      !isDesktop &&
      containerRef.current.clientHeight > 0
    ) {
      const containerTop = containerRef.current.getBoundingClientRect().top;
      const distanceFromTopToBottom = window.innerHeight - containerTop;
      updateMiniPlayerStyles({ bottom: distanceFromTopToBottom });
    }

    return () => {
      if (!isDesktop) {
        updateMiniPlayerStyles({ bottom: undefined });
      }
    };
  }, [containerRef.current?.clientHeight, isDesktop]);

  if (hiddenOnDesktop && isDesktop) return null;

  return (
    <div
      ref={isDesktop ? undefined : containerRef}
      className={cn(
        isDesktop
          ? "sticky top-0 flex h-screen w-full justify-end bg-transparent"
          : "relative bg-white/95 backdrop-blur-md border-t border-secondary-light-grey"
      )}
    >
      <nav
        className={cn(
          "flex shrink-0 items-stretch",
          isDesktop
            ? "size-full  max-w-64 flex-col justify-start gap-2 p-6"
            : "flex-row justify-between"
        )}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = pathname.startsWith(item.activePrefix);

          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => {
                navClickBottomBar(item.analyticsName, "public app");
                if (
                  item.analyticsName === AnalyticsNavElementName.DISCOVERY &&
                  isActive
                ) {
                  scrollDiscoveryToTop();
                }
              }}
              className={cn(
                "flex items-center transition-colors duration-200",
                "[&_svg]:size-7 [&_svg]:shrink-0",
                isDesktop
                  ? "gap-3 rounded-2xl px-3 py-4"
                  : "flex-1 flex-col gap-1.5 pb-4 pt-2.5",
                isActive ? "[&_svg]:opacity-100" : "[&_svg]:opacity-40"
              )}
            >
              <Badge show={badges[item.label]} position="br">
                {isActive ? (
                  <item.ActiveIcon className="text-primary-black" />
                ) : (
                  <item.InactiveIcon className="text-primary-black" />
                )}
              </Badge>
              <span
                className={cn(
                  "font-dm-sans leading-none text-primary-black",
                  isDesktop
                    ? "text-sm font-semibold whitespace-nowrap"
                    : "max-w-[70%] text-center text-[9px] font-bold",
                  isActive ? "opacity-100" : "opacity-40"
                )}
              >
                {navLabels[item.label]}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * Loading silhouette for the mobile {@link Navbar} as presented in
 * ScrollableLayout's footer: the top border + the footer's
 * `calc(safe-area-bottom / 2)` inset (so the bar clears the home indicator on
 * notched devices), with one column per nav item (28px icon + 9/13.5 label
 * line). Driving the columns off {@link NAV_ITEMS} keeps the count in lockstep
 * with the real nav — add a tab and the skeleton follows automatically.
 */
export function NavbarSkeleton(): React.JSX.Element {
  return (
    <div
      className="flex flex-row justify-between border-t border-secondary-light-grey bg-white"
      style={{ paddingBottom: "calc(var(--safe-area-bottom) / 2)" }}
    >
      {NAV_ITEMS.map((item) => (
        <div
          key={item.to}
          className="flex flex-1 flex-col items-center gap-1.5 pt-2.5 pb-4"
        >
          <Skeleton $variant="circular" width={28} height={28} />
          <SkeletonTextLine width={32} fontSize={9} lineHeight={13.5} />
        </div>
      ))}
    </div>
  );
}
